import { useState } from 'react';
import SimulatorForm from './components/SimulatorForm';
import ResultCard from './components/ResultCard';
import DistributionChart from './components/DistributionChart';
import ModelsComparison from './components/ModelsComparison';
import SaveConfigModal from './components/SaveConfigModal';
import AlertModal from './components/AlertModal';
import {
    simulateReferralProgram,
    compareModels,
    createConfig,
} from './api';
import './referral-simulator.css';

// Translate backend / network errors to Persian
const translateBackendError = (message) => {
    if (!message) return 'خطای نامشخص';

    // Try to parse JSON (Pydantic errors array)
    try {
        const parsed = JSON.parse(message);
        if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].msg) {
            return translateBackendError(parsed[0].msg);
        }
    } catch {
        // not JSON
    }

    // If already in Persian, return as-is
    if (/[\u0600-\u06FF]/.test(message)) {
        return message;
    }

    // Generic Pydantic patterns
    let m = message.match(/Input should be less than or equal to ([0-9.]+)/i);
    if (m) return `مقدار وارد شده باید کمتر یا مساوی ${m[1]} باشد.`;

    m = message.match(/Input should be greater than or equal to ([0-9.]+)/i);
    if (m) return `مقدار وارد شده باید بزرگ‌تر یا مساوی ${m[1]} باشد.`;

    m = message.match(/Input should be greater than ([0-9.]+)/i);
    if (m) return `مقدار وارد شده باید بزرگ‌تر از ${m[1]} باشد.`;

    m = message.match(/Input should be less than ([0-9.]+)/i);
    if (m) return `مقدار وارد شده باید کمتر از ${m[1]} باشد.`;

    if (/Input should be a valid number/i.test(message)) {
        return 'مقدار وارد شده باید عدد باشد.';
    }
    if (/Field required/i.test(message)) {
        return 'تکمیل این فیلد الزامی است.';
    }
    if (/Could not validate credentials/i.test(message)) {
        return 'نشست شما منقضی شده است. لطفاً دوباره وارد شوید.';
    }

    // Field name mapping (if loc is in message)
    m = message.match(/^body\.(\w+)[:.\s]/i);
    if (m) {
        const fieldMap = {
            sales_amount: 'مبلغ سبد کالا',
            margin_percent: 'حاشیه سود',
            min_margin_percent: 'حداقل حاشیه سود',
            max_depth: 'حداکثر عمق زنجیره',
            safety_factor: 'حداکثر درصد پرداخت از سود',
            max_sales_percent: 'حداکثر مجموع پورسانت',
        };
        const label = fieldMap[m[1]] || m[1];
        return `${label}: ${message.replace(/^body\.\w+[:.\s]+/i, '')}`;
    }

    return message;
};

export default function ReferralSimulator() {
    const [result, setResult] = useState(null);
    const [comparison, setComparison] = useState(null);
    const [lastPayload, setLastPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [compareLoading, setCompareLoading] = useState(false);
    const [showComparison, setShowComparison] = useState(false);

    const [showSaveModal, setShowSaveModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState('');

    // Alert modal state
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [alertTitle, setAlertTitle] = useState('');

    const showAlert = (message, title = 'خطا در اعتبارسنجی') => {
        setAlertTitle(title);
        setAlertMessage(message);
        setAlertOpen(true);
    };

    const handleSubmit = async (payload) => {
        setLoading(true);
        try {
            const data = await simulateReferralProgram(payload);
            setResult(data);
            setLastPayload(payload);
            setShowComparison(false);
        } catch (e) {
            const translated = translateBackendError(e.message);
            showAlert(translated, 'خطا در شبیه‌سازی');
            setResult(null);
        } finally {
            setLoading(false);
        }
    };

    const handleCompare = async (payload) => {
        setCompareLoading(true);
        try {
            const comparePayload = Object.fromEntries(
                Object.entries(payload).filter(
                    ([key]) => key !== 'distribution' && key !== 'custom_weights',
                ),
            );
            const data = await compareModels(comparePayload);
            setComparison(data);
            setShowComparison(true);
            setLastPayload(payload);
        } catch (e) {
            const translated = translateBackendError(e.message);
            showAlert(translated, 'خطا در مقایسه مدل‌ها');
        } finally {
            setCompareLoading(false);
        }
    };

    const handleSave = async ({ name, description }) => {
        if (!lastPayload || !result) return;

        setSaving(true);
        try {
            const levelPercents = {};
            result.levels.forEach((lvl) => {
                levelPercents[String(lvl.level)] = Number(lvl.percent);
            });

            await createConfig({
                name,
                description,
                buyer_discount_percent: 0,
                level_percents: levelPercents,
                max_depth: lastPayload.max_depth,
                max_total_percent: result.caps.final_cap,
                min_margin_percent: lastPayload.min_margin_percent,
                margin_percent: lastPayload.margin_percent,
                requires_active: false,
                active_days_window: 30,
                min_purchase_count: 1,
                is_active: false,
            });

            setSaveSuccess(
                'برنامه با موفقیت ذخیره شد. از بخش «تنظیمات پورسانت» می‌توانید فعالش کنید.'
            );
            setShowSaveModal(false);
            setTimeout(() => setSaveSuccess(''), 6000);
        } catch (e) {
            showAlert(translateBackendError(e.message), 'خطا در ذخیره برنامه');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="referral-simulator">
            <header className="page-header">
                <h1>شبیه‌ساز پورسانت زنجیره‌ای</h1>
                <p>قبل از فعال‌سازی، اثر پورسانت‌ها روی سود را بررسی کنید.</p>
            </header>

            {saveSuccess && (
                <div className="page-banner page-banner-success">
                    <span className="page-banner-icon">✓</span>
                    <span className="page-banner-text">{saveSuccess}</span>
                    <button
                        className="page-banner-close"
                        onClick={() => setSaveSuccess('')}
                        aria-label="بستن"
                    >
                        ✕
                    </button>
                </div>
            )}

            <section className="form-section">
                <SimulatorForm
                    onSubmit={handleSubmit}
                    onCompare={handleCompare}
                    loading={loading || compareLoading}
                    onValidationError={showAlert}
                />
            </section>

            <section className="result-section">
                {!showComparison && (
                    <>
                        <ResultCard result={result} />
                        {result && <DistributionChart levels={result.levels} />}

                        {result && (
                            <div className="save-actions">
                                <button
                                    type="button"
                                    className="btn-save-config"
                                    onClick={() => setShowSaveModal(true)}
                                >
                                    💾 ذخیره به عنوان برنامه پورسانت
                                </button>
                            </div>
                        )}
                    </>
                )}

                {showComparison && <ModelsComparison data={comparison} />}
            </section>

            {showSaveModal && (
                <SaveConfigModal
                    onClose={() => setShowSaveModal(false)}
                    onSave={handleSave}
                    loading={saving}
                    defaultName="برنامه شبیه‌سازی‌شده"
                />
            )}

            <AlertModal
                open={alertOpen}
                title={alertTitle}
                message={alertMessage}
                onClose={() => setAlertOpen(false)}
            />
        </div>
    );
}